
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Request} from '../core/Request';
import {FieldModelRequest} from '../core/Request';
import {OrdersGetPageResponse} from './OrdersGetPageResponse';
import {OrdersDisjunctionFiltration} from '../core/OrdersDisjunctionFiltration';
import {FieldModelOrdersDisjunctionFiltration} from '../core/OrdersDisjunctionFiltration';
import {OrdersFiltration} from '../core/OrdersFiltration';
import {FieldModelOrdersFiltration} from '../core/OrdersFiltration';
import {OrdersSorting} from '../core/OrdersSorting';
import {FieldModelOrdersSorting} from '../core/OrdersSorting';

/**
 * OrdersGetPageRequest struct
 */
class OrdersGetPageRequest extends Request {
  /**
   * Initialize struct
   * @param {!Request=} parent
   * @param {!OrdersDisjunctionFiltration=} ordersDisjunctionFiltration
   * @param {!OrdersFiltration=} ordersFiltration
   * @param {!OrdersSorting=} ordersSorting
   * @param {!number=} pageSize
   * @param {!number=} pageNumberZeroBased
   * @constructor
   */
  constructor (parent = new Request(), ordersDisjunctionFiltration = new OrdersDisjunctionFiltration(), ordersFiltration = new OrdersFiltration(), ordersSorting = new OrdersSorting(), pageSize = new UInt64(20, 0), pageNumberZeroBased = new UInt64(0, 0)) {
    super()
    super.copy(parent)
    this.OrdersDisjunctionFiltration = ordersDisjunctionFiltration
    this.OrdersFiltration = ordersFiltration
    this.OrdersSorting = ordersSorting
    this.PageSize = pageSize
    this.PageNumberZeroBased = pageNumberZeroBased
  }

  /**
   * Copy struct (shallow copy)
   * @this {!OrdersGetPageRequest}
   * @param {!OrdersGetPageRequest} other Other struct
   * @returns {!OrdersGetPageRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.OrdersDisjunctionFiltration != null) {
      this.OrdersDisjunctionFiltration = OrdersDisjunctionFiltration.fromObject(other.OrdersDisjunctionFiltration)
    } else {
      this.OrdersDisjunctionFiltration = null
    }
    if (other.OrdersFiltration != null) {
      this.OrdersFiltration = OrdersFiltration.fromObject(other.OrdersFiltration)
    } else {
      this.OrdersFiltration = null
    }
    if (other.OrdersSorting != null) {
      this.OrdersSorting = OrdersSorting.fromObject(other.OrdersSorting)
    } else {
      this.OrdersSorting = null
    }
    if (other.PageSize != null) {
      this.PageSize = UInt64.fromNumber(other.PageSize)
    } else {
      this.PageSize = null
    }
    if (other.PageNumberZeroBased != null) {
      this.PageNumberZeroBased = UInt64.fromNumber(other.PageNumberZeroBased)
    } else {
      this.PageNumberZeroBased = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!OrdersGetPageRequest}
   * @returns {!OrdersGetPageRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new OrdersGetPageRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new OrdersGetPageRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!OrdersGetPageRequest}
   * @param {!OrdersGetPageRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof OrdersGetPageRequest)) {
      throw new TypeError('Instance of OrdersGetPageRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!OrdersGetPageRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      OrdersDisjunctionFiltration: ((this.OrdersDisjunctionFiltration != null) ? this.OrdersDisjunctionFiltration : null),
      OrdersFiltration: ((this.OrdersFiltration != null) ? this.OrdersFiltration : null),
      OrdersSorting: ((this.OrdersSorting != null) ? this.OrdersSorting : null),
      PageSize: ((this.PageSize != null) ? this.PageSize.toNumber() : null),
      PageNumberZeroBased: ((this.PageNumberZeroBased != null) ? this.PageNumberZeroBased.toNumber() : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return OrdersGetPageRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!OrdersGetPageRequest} other Object value
   * @returns {!OrdersGetPageRequest} Created struct
   */
  static fromObject (other) {
    return new OrdersGetPageRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!OrdersGetPageRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return OrdersGetPageRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!OrdersGetPageRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6470
  }
}

export { OrdersGetPageRequest };

/**
 * Fast Binary Encoding OrdersGetPageRequest field model
 */
class FieldModelOrdersGetPageRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelRequest(buffer, 4 + 4)
    this.OrdersDisjunctionFiltration = new FieldModelOrdersDisjunctionFiltration(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.OrdersFiltration = new FieldModelOrdersFiltration(buffer, this.OrdersDisjunctionFiltration.fbeOffset + this.OrdersDisjunctionFiltration.fbeSize)
    this.OrdersSorting = new FieldModelOrdersSorting(buffer, this.OrdersFiltration.fbeOffset + this.OrdersFiltration.fbeSize)
    this.PageSize = new fbe.FieldModelUInt64(buffer, this.OrdersSorting.fbeOffset + this.OrdersSorting.fbeSize)
    this.PageNumberZeroBased = new fbe.FieldModelUInt64(buffer, this.PageSize.fbeOffset + this.PageSize.fbeSize)
  }

  /**
   * Get the core.Request field model
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!FieldModelRequest} core.Request field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.OrdersDisjunctionFiltration.fbeSize + this.OrdersFiltration.fbeSize + this.OrdersSorting.fbeSize + this.PageSize.fbeSize + this.PageNumberZeroBased.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.OrdersDisjunctionFiltration.fbeExtra + this.OrdersFiltration.fbeExtra + this.OrdersSorting.fbeExtra + this.PageSize.fbeExtra + this.PageNumberZeroBased.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelOrdersGetPageRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6470
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.OrdersDisjunctionFiltration.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrdersDisjunctionFiltration.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrdersDisjunctionFiltration.fbeSize

    if ((fbeCurrentSize + this.OrdersFiltration.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrdersFiltration.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrdersFiltration.fbeSize

    if ((fbeCurrentSize + this.OrdersSorting.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrdersSorting.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrdersSorting.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PageSize.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.PageNumberZeroBased.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PageNumberZeroBased.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageNumberZeroBased.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!OrdersGetPageRequest} fbeValue Default value, defaults is new OrdersGetPageRequest()
   * @returns {!OrdersGetPageRequest} OrdersGetPageRequest value
   */
  get (fbeValue = new OrdersGetPageRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!OrdersGetPageRequest} fbeValue OrdersGetPageRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.OrdersDisjunctionFiltration.fbeSize) <= fbeStructSize) {
      fbeValue.OrdersDisjunctionFiltration = this.OrdersDisjunctionFiltration.get()
    } else {
      fbeValue.OrdersDisjunctionFiltration = new OrdersDisjunctionFiltration()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrdersDisjunctionFiltration.fbeSize

    if ((fbeCurrentSize + this.OrdersFiltration.fbeSize) <= fbeStructSize) {
      fbeValue.OrdersFiltration = this.OrdersFiltration.get()
    } else {
      fbeValue.OrdersFiltration = new OrdersFiltration()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrdersFiltration.fbeSize

    if ((fbeCurrentSize + this.OrdersSorting.fbeSize) <= fbeStructSize) {
      fbeValue.OrdersSorting = this.OrdersSorting.get()
    } else {
      fbeValue.OrdersSorting = new OrdersSorting()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrdersSorting.fbeSize

    if ((fbeCurrentSize + this.PageSize.fbeSize) <= fbeStructSize) {
      fbeValue.PageSize = this.PageSize.get(new UInt64(20, 0))
    } else {
      fbeValue.PageSize = new UInt64(20, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageSize.fbeSize

    if ((fbeCurrentSize + this.PageNumberZeroBased.fbeSize) <= fbeStructSize) {
      fbeValue.PageNumberZeroBased = this.PageNumberZeroBased.get(new UInt64(0, 0))
    } else {
      fbeValue.PageNumberZeroBased = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PageNumberZeroBased.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelOrdersGetPageRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {!OrdersGetPageRequest} fbeValue OrdersGetPageRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelOrdersGetPageRequest}
   * @param {OrdersGetPageRequest} fbeValue OrdersGetPageRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.OrdersDisjunctionFiltration.set(fbeValue.OrdersDisjunctionFiltration)
    this.OrdersFiltration.set(fbeValue.OrdersFiltration)
    this.OrdersSorting.set(fbeValue.OrdersSorting)
    this.PageSize.set(fbeValue.PageSize)
    this.PageNumberZeroBased.set(fbeValue.PageNumberZeroBased)
  }
}

export { FieldModelOrdersGetPageRequest };

/**
 * Fast Binary Encoding OrdersGetPageRequest model
 */
class OrdersGetPageRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelOrdersGetPageRequest(this.buffer, 4)
  }

  /**
   * Get the OrdersGetPageRequest model
   * @this {!OrdersGetPageRequestModel}
   * @returns {!FieldModelOrdersGetPageRequest} model OrdersGetPageRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!OrdersGetPageRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!OrdersGetPageRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return OrdersGetPageRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!OrdersGetPageRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelOrdersGetPageRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!OrdersGetPageRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!OrdersGetPageRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!OrdersGetPageRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!OrdersGetPageRequestModel}
   * @param {!OrdersGetPageRequest} value OrdersGetPageRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!OrdersGetPageRequestModel}
   * @param {!OrdersGetPageRequest} value OrdersGetPageRequest value, defaults is new OrdersGetPageRequest()
   * @return {!object} Deserialized OrdersGetPageRequest value and its size
   */
  deserialize (value = new OrdersGetPageRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new OrdersGetPageRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new OrdersGetPageRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!OrdersGetPageRequestModel}
   * @param {!number} prev Previous OrdersGetPageRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { OrdersGetPageRequestModel };
OrdersGetPageRequest.__has_response = true;
OrdersGetPageRequest.__response_class = OrdersGetPageResponse;
